import { T, useTranslate } from '@tolgee/react';
import { notification, Tooltip } from 'antd';
import { delay } from 'lodash';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Hotkeys from 'react-hot-keys';
import { useIntercom } from 'react-use-intercom';
import styled, { css } from 'styled-components';

import { PlusSvg } from '../../../assets/icons';
import MinusSvg from '../../../assets/icons/MinusSvg';
import theme from '../../../assets/theme';
import useAccessBadge from '../../../hooks/useAccessBadge';
import useAiAssessedScores from '../../../hooks/useAiAssessedScores';
import useAttioEventTracking from '../../../hooks/useAttioEventTracking';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import useGameSaveSocket from '../../../hooks/useGameSaveSocket';
import { useInterval } from '../../../hooks/useInterval';
import { useMetrics } from '../../../hooks/useMetrics';
import useResponses from '../../../hooks/useResponses';
import { useRoundCompletedListener } from '../../../hooks/useRoundCompletedListener';
import useRouterMatch from '../../../hooks/useRouterMatch';
import useTemplate from '../../../hooks/useTemplate';
import useTrackActivation from '../../../hooks/useTrackActivation';
import { LiveActionsDisabledContext } from '../../../providers/LiveActionsDisabledProvider/LiveActionsDisabledProvider';
import { SocketContext } from '../../../providers/SocketProvider/SocketProvider';
import { TimesyncContext } from '../../../providers/TimesyncProvider/TimesyncProvider';
import { Game } from '../../../services/backendService/types';
import Metrics from '../../../services/metrics';
import { debounceEventIfNotTrusted, notUndefined } from '../../../utils/general';
import { RoundState } from '../../../utils/roundUtils';
import { formatSubjectForSentence } from '../../../utils/subjectUtils';
import { zIndexes } from '../../../utils/zIndexes';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { LiveCountdown } from '../../_atoms/LiveCountdown';
import { getPromptTemplate } from '../../_organisms/DesignerTabPane/PromptsTab/newPromptTemplates';
import { calculateRemainingTime } from '../../_organisms/LiveGame';
import { Slide } from '../../SlideComponents/slide.types';
import { findRelatedSlidesWithoutIndex } from '../../SlideComponents/slideUtil';
import ActivitySpecificResponseSummary from './ActivitySpecificResponseSummary';
import LiveCounterWidget from './LiveCounterWidget';

const SimpleLiveGameWidgetV2 = ({
  game,
  currentSlide,
  onCountDownCompleted,
  setShowMedia,
  canStartActivity,
  tenantId,
  roundState,
  moderationTooltipHasBeenShown,
  setModerationTooltipHasBeenShown,
  participantCount,
  showAIFeedbackOverlay,
  setShowAIFeedbackOverlay,
  hasModalBeenOpened,
  isFeedbackGivenForSlide,
  markFeedbackGivenForSlide,
}: {
  game: Game;
  setShowMedia: Dispatch<SetStateAction<boolean>>;
  currentSlide: Slide;
  canStartActivity: boolean;
  roundState?: RoundState;
  onCountDownCompleted: () => void;
  tenantId: string;
  moderationTooltipHasBeenShown: boolean;
  setModerationTooltipHasBeenShown: Dispatch<SetStateAction<boolean>>;
  participantCount: number;
  showAIFeedbackOverlay?: boolean;
  setShowAIFeedbackOverlay?: Dispatch<SetStateAction<boolean>>;
  hasModalBeenOpened?: boolean;
  isFeedbackGivenForSlide?: (slideId: string) => boolean;
  markFeedbackGivenForSlide?: (slideId: string) => void;
}) => {
  const metrics = useMetrics();
  const { trackEvent } = useIntercom();
  const { t } = useTranslate();
  const { autoSave } = useGameSaveSocket();
  useAccessBadge();
  useAttioEventTracking();
  const [showModerationTooltip, setShowModerationTooltip] = useState(false);
  const { now } = useContext(TimesyncContext);
  const { trackActivation } = useTrackActivation();
  const { gameAPI } = useContext(SocketContext);
  const { openFirstAiResponse } = useContext(LiveActionsDisabledContext);

  const { template, autoSaveTemplate } = useTemplate(game.templateId);
  const isFrozen = useMemo(() => !!game.roundFreezedAt, [game.roundFreezedAt]);

  const relatedSlides = useMemo(
    () =>
      currentSlide && game.slides
        ? findRelatedSlidesWithoutIndex(currentSlide.id, game.slides)
        : undefined,
    [currentSlide, game.slides],
  );

  // Find the slide ID that actually has the responses
  // For result slides (responsegrid, podium), use findRelatedSlidesWithoutIndex to find
  // the chain of related slides. The first slide in the chain is the one with responses.
  // For regular slides, the current slide itself has the responses.
  const responseSlideId = useMemo(() => {
    if (!currentSlide || !game.slides) {
      return currentSlide?.id;
    }

    const relatedSlides = findRelatedSlidesWithoutIndex(currentSlide.id, game.slides);
    if (relatedSlides.length > 0) {
      // The first slide in the related slides chain is the one with responses
      return relatedSlides[0].id;
    }

    // Otherwise, the current slide itself has the responses
    return currentSlide.id;
  }, [currentSlide, game.slides]);

  // Hook to generate AI feedback
  // Use responseSlideId to ensure we're using the same slide ID as StreamingLongAnswerList
  const slideIdForAiScores = responseSlideId || currentSlide?.id || '';
  const {
    generateAllAiAssessedScores,
    isFetchingAiAssessedScores,
    hasStartedGenerating,
  } = useAiAssessedScores({
    gameId: game.id,
    slideId: slideIdForAiScores,
    tenantId,
  });

  // Get responses to check if we can open the first one
  const { responses } = useResponses(game.id, slideIdForAiScores, true, tenantId);

  // Use hasModalBeenOpened from props (managed in LiveGame)
  const modalOpened = hasModalBeenOpened ?? false;
  useRoundCompletedListener(
    onCountDownCompleted,
    game.id,
    tenantId,
    game.currentRound,
    participantCount,
  );
  const handleEnterPressed = () => {
    metrics.logEvent('LiveGame.SpaceOrEnter.Pressed');
    if (roundState === 'post-between' && nextSlide?.type !== 'voting') {
      handleSeeResults();
      return;
    }
    handleStartPressed();
  };

  const nextSlide = useMemo(() => {
    if (game.slides && currentSlide) {
      const currentRoundIndex = game.slides?.findIndex(r => r.id === currentSlide.id);
      if (currentRoundIndex >= 0 && currentRoundIndex + 1 <= game.slides?.length) {
        return game.slides[currentRoundIndex + 1];
      }
    }
  }, [currentSlide, game.slides]);

  // Check if current slide is a response grid for an AI assessed activity
  const isAIFeedbackResultsSlide = useMemo(() => {
    if (!currentSlide || currentSlide.type !== 'responsegrid') return false;
    // Find the activity slide that this results slide is for
    const activitySlide = relatedSlides?.[0];
    return (
      activitySlide?.type === 'aiAssessedAnswers' ||
      activitySlide?.type === 'aiAssessedImageAnswers'
    );
  }, [currentSlide, relatedSlides]);

  const isLargerThan600 = useBreakPoint(600);
  useEffect(() => {
    if (!isLargerThan600) {
      notification.warning({
        message: t('Playing a curipod works best on larger devices'),
        top: 10,
      });
    }
  }, [isLargerThan600, t]);
  const [secondsLeft, setSecondsLeft] = useState<number | undefined>();

  useEffect(() => {
    if (
      game.roundFreezedAt &&
      game.roundStartsAt &&
      currentSlide.durationSeconds &&
      roundState === 'in'
    ) {
      const durationOfFreeze = now() - game.roundFreezedAt;
      const end = new Date(
        game.roundStartsAt + currentSlide.durationSeconds * 1000,
      ).getTime();
      const remainingTime = Math.floor((end - now() - durationOfFreeze) / 1000);
      setSecondsLeft(remainingTime);
    }
  }, [
    currentSlide.durationSeconds,
    game.roundFreezedAt,
    game.roundStartsAt,
    now,
    roundState,
    secondsLeft,
  ]);

  useInterval(() => {
    if (!!game.roundFreezedAt && (roundState === 'in' || roundState === 'countdown')) {
      return;
    }
    if (roundState === 'post-between' && nextSlide) {
      setSecondsLeft(nextSlide?.durationSeconds || 30);
      return;
    }

    if (
      game.roundStartsAt &&
      currentSlide &&
      currentSlide.durationSeconds &&
      roundState !== 'post-between'
    ) {
      const end = new Date(game.roundStartsAt + currentSlide.durationSeconds * 1000);
      setSecondsLeft(calculateRemainingTime(now, end.getTime()));
      return;
    }

    if (currentSlide.durationSeconds) {
      setSecondsLeft(currentSlide.durationSeconds);
      return;
    }
    if (currentSlide.durationSeconds) {
      setSecondsLeft(currentSlide.durationSeconds);
      return;
    }
  }, 1000 / 60);
  useEffect(() => {
    if (roundState === 'in' && secondsLeft && secondsLeft <= 0) {
      if (!!game.roundFreezedAt) return;
      // Automatically start generating feedback when timer runs out
      const isAIAssessedActivity =
        currentSlide.type === 'aiAssessedAnswers' ||
        currentSlide.type === 'aiAssessedImageAnswers';
      if (isAIAssessedActivity && gameAPI && !isFetchingAiAssessedScores) {
        // Start generating feedback in the background
        generateAllAiAssessedScores();
      }
      onCountDownCompleted();
    }
  }, [
    game.roundFreezedAt,
    onCountDownCompleted,
    roundState,
    secondsLeft,
    currentSlide.type,
    gameAPI,
    isFetchingAiAssessedScores,
    generateAllAiAssessedScores,
  ]);

  // Handle page refresh scenario: If we're in post/post-between state with an AI assessed activity
  // and generation hasn't started yet but there are responses, start generation
  useEffect(() => {
    const isAIAssessedActivity =
      currentSlide.type === 'aiAssessedAnswers' ||
      currentSlide.type === 'aiAssessedImageAnswers';

    const isPostRound = roundState === 'post' || roundState === 'post-between';
    const hasResponses = responses && responses.length > 0;

    // Only generate if:
    // - It's an AI assessed activity
    // - We're in a post-round state (timer has ended)
    // - We have responses to assess
    // - Generation hasn't started yet (checked via localStorage)
    // - We're not currently fetching
    // - gameAPI is available
    if (
      isAIAssessedActivity &&
      isPostRound &&
      hasResponses &&
      !hasStartedGenerating &&
      !isFetchingAiAssessedScores &&
      gameAPI
    ) {
      // This handles the case where the page was refreshed after the timer ended
      // but generation hadn't started or was interrupted
      // The mutationFn inside generateAllAiAssessedScores checks localStorage again
      generateAllAiAssessedScores();
    }
  }, [
    currentSlide.type,
    roundState,
    responses,
    hasStartedGenerating,
    isFetchingAiAssessedScores,
    gameAPI,
    generateAllAiAssessedScores,
  ]);

  const handleGoNextSlide = useCallback(() => {
    if (game && game.slides) {
      const currentSlideIndex = game.currentRound
        ? game.slides.findIndex(s => s.id === game.currentRound)
        : -1;
      const currentSlide = game.slides[currentSlideIndex];
      const newSlides =
        currentSlideIndex !== -1
          ? game.slides.map(r =>
              r.id === currentSlide.id ? { ...r, wasPlayed: true } : r,
            )
          : game.slides;
      const nextSlide = game.slides[currentSlideIndex + 1];

      autoSave({
        game: {
          ...game,
          slides: newSlides,
          currentRound: nextSlide.id,
          roundFreezedAt: null,
          roundStartsAt:
            nextSlide.type === 'voting' && !nextSlide.wasPlayed ? now() + 3700 : null,
          modifiedAt: now(),
          isShowingLobby: false,
        },
      });
    }
  }, [autoSave, game, now]);

  const handleEndSlide = useCallback(() => {
    if (game && game.slides && currentSlide) {
      const newSlides = game?.slides.map(r =>
        r.id === currentSlide.id
          ? {
              ...currentSlide,
              // updated duration is the current lasted amount of seconds of the round + 3 seconds
              durationSeconds: (now() - (game.roundStartsAt || 0)) / 1000 + 3,
            }
          : r,
      );
      autoSave({
        game: {
          ...game,
          slides: newSlides,
          modifiedAt: now(),
          roundFreezedAt: null,
        },
      });
    }
  }, [game, autoSave, currentSlide, now]);

  const handleStartActivity = useCallback(() => {
    if (game && game.slides) {
      setShowMedia(false);
      if (game.isPlaying === false) {
        const generatorUsed = game.slides
          .map(s => s.generatorId)
          .filter(notUndefined)
          .filter((value, index, self) => self.indexOf(value) === index); // filters out duplicates

        // Logging active game for the first time a game is played
        const metadata = {
          isFromDiscover: game.isFromDiscover,
          participants: participantCount,
          rounds: 0,
          slidesCount: game.slides.length,
          gameId: game.id,
          templateId: game.templateId,
          generatorUsed: generatorUsed,
          grade: template?.metadata?.grade,
          subject: template?.metadata?.subject,
          isDynamic: template?.metadata?.isDynamic,
          language: template?.metadata?.language,
          selectedCardId: template?.metadata?.selectedCardId,
          uploadedSlidesFileId: template?.metadata?.uploadedSlidesFileId,
          blocks: game.slides.map(s => s.blockLabel).filter(notUndefined),
          blockIds: game.slides.map(s => s.blockId).filter(notUndefined),
          isActivities: template?.metadata?.isActivities,
        };

        metrics.logGameEvent('Live.Start', metadata);
        if (participantCount > 2) {
          Metrics.incrementGamePlayed();
          trackEvent('StartGame', metadata);
          trackActivation({
            userAgent: navigator.userAgent,
            eventType: 'RealLiveLessonStarted',
          });
        } else {
          Metrics.incrementTestGamePlayed();
          trackEvent('StartTestGame', metadata);
        }

        // Logging the unique generators that are used for this lesson
        const allGeneratorIds = game.slides.map(s => s.generatorId).filter(notUndefined);
        const uniqueGeneratorIds = Array.from(new Set(allGeneratorIds));

        uniqueGeneratorIds.forEach(generatorId => {
          metrics.logGameEvent('Live.Start.Generator.Played', {
            generatorId,
            participants: participantCount,
          });
        });
      }
      const currentSlide = game.slides.find(r => r.id === game.currentRound);

      if (currentSlide) {
        const slidePrompt = currentSlide.prompts?.[0];
        const slidePromptTemplate = getPromptTemplate(slidePrompt);
        //logs activity type
        metrics.logGameEvent('Live.Start.ActivityType', {
          activityType: currentSlide.type,
          tenantId,
          gameId: game.id,
          participants: participantCount,
          generatorId: currentSlide.generatorId,
          slidePromptTemplate,
          block: currentSlide.blockLabel,
          blockId: currentSlide.blockId,
          grade: template?.metadata?.grade,
          subject: template?.metadata?.subject,
          language: template?.metadata?.language,
          templateId: game.templateId,
        });

        trackEvent('LiveStartActivityType', {
          activityType: currentSlide.type,
          subject: formatSubjectForSentence(template?.metadata?.subject),
          tenantId,
          gameId: game.id,
          participants: participantCount,
          generatorId: currentSlide.generatorId,
          blockId: currentSlide.blockId,
        });

        if (currentSlide.generatorId) {
          // Logs the single generator that is used for this activity
          metrics.logGameEvent('Live.Start.SingleSlide.Generator', {
            generatorId: currentSlide.generatorId,
            gameId: game.id,
            participants: participantCount,
          });

          if (participantCount > 2) {
            Metrics.incrementGeneratorPlayed();
          }
        }
      }

      autoSave({
        game: {
          ...game,
          isPlaying: true,
          roundStartsAt: now() + 3700,
          modifiedAt: now(),
          lastPlayedAt: game.lastPlayedAt || now(),
          roundFreezedAt: null,
          isShowingLobby: false,
        },
        onSuccessFunction: () => {
          if (
            !game.isFromDiscover &&
            template &&
            !template.content.activeGame?.isOngoing &&
            game.templateId
          ) {
            autoSaveTemplate({
              templateConfig: {
                // make sure isPreview doesnt alter activeGame
                activeGame: game.isPreviewGame
                  ? undefined
                  : {
                      id: game.id,
                      isOngoing: true,
                    },
                id: game.templateId,
                modifiedAt: new Date().getTime(),
                gameIds: !game.isPreviewGame ? [game.id] : [],
              },
            });
          }
        },
      });
    }
  }, [
    autoSave,
    autoSaveTemplate,
    game,
    metrics,
    now,
    setShowMedia,
    template,
    participantCount,
    tenantId,
    trackEvent,
    trackActivation,
  ]);

  const handleStartPressed = useCallback(() => {
    setShowMedia(false);

    if (game.roundFreezedAt && roundState === 'in') {
      // resuming a paused round
      autoSave({
        game: {
          ...game,
          roundFreezedAt: null,
          modifiedAt: now(),
        },
      });
      return;
    }
    if (canStartActivity) {
      handleStartActivity();
    } else if (roundState === 'post-between') {
      handleGoNextSlide();
    }
  }, [
    setShowMedia,
    game,
    canStartActivity,
    roundState,
    autoSave,
    now,
    handleStartActivity,
    handleGoNextSlide,
  ]);

  const handleSeeResults = useCallback(() => {
    const resultsSlide = relatedSlides?.[relatedSlides.length - 1];
    metrics.logEvent('LiveGame.SeeResults.Clicked');

    // If overlay is showing, simply close it (regardless of which slide we're on)
    if (showAIFeedbackOverlay) {
      setShowAIFeedbackOverlay?.(false);
      return;
    }

    // Check if this is an AI assessed activity
    const isAIAssessedActivity =
      currentSlide.type === 'aiAssessedAnswers' ||
      currentSlide.type === 'aiAssessedImageAnswers';

    if (isAIAssessedActivity) {
      const feedbackGiven = isFeedbackGivenForSlide?.(currentSlide.id) ?? false;

      // If feedback hasn't been given yet, navigate to results and show overlay immediately
      // (Feedback should already be generating in the background from when timer ran out)
      // Only show overlay if there are responses to review
      if (!feedbackGiven && responses && responses.length > 0) {
        markFeedbackGivenForSlide?.(currentSlide.id);
        setShowAIFeedbackOverlay?.(true);
        if (resultsSlide) {
          autoSave({
            game: {
              ...game,
              modifiedAt: now(),
              currentRound: resultsSlide.id,
              roundFreezedAt: null,
            },
          });
        }
        return;
      } else if (!feedbackGiven && (!responses || responses.length === 0)) {
        // If no responses, just navigate to results without showing overlay
        markFeedbackGivenForSlide?.(currentSlide.id);
        if (resultsSlide) {
          autoSave({
            game: {
              ...game,
              modifiedAt: now(),
              currentRound: resultsSlide.id,
              roundFreezedAt: null,
            },
          });
        }
        return;
      }
    }

    // Default behavior
    if (resultsSlide)
      autoSave({
        game: {
          ...game,
          modifiedAt: now(),
          currentRound: resultsSlide.id,
          roundFreezedAt: null,
        },
      });
  }, [
    autoSave,
    game,
    metrics,
    now,
    relatedSlides,
    currentSlide,
    showAIFeedbackOverlay,
    setShowAIFeedbackOverlay,
    responses,
    isFeedbackGivenForSlide,
    markFeedbackGivenForSlide,
  ]);

  const handleEditSlideDuration = useCallback(
    (editAmount: number) => {
      const slideToEdit = roundState === 'post-between' ? nextSlide : currentSlide;
      if (game && game.slides && slideToEdit) {
        metrics.logEvent('LiveGame.EditRoundDuration.Clicked');
        const newSlides = game?.slides.map(r =>
          r.id === slideToEdit.id
            ? {
                ...slideToEdit,
                durationSeconds: (slideToEdit.durationSeconds || 0) + editAmount,
              }
            : r,
        );
        autoSave({
          game: {
            ...game,
            slides: newSlides,
            modifiedAt: now(),
          },
        });
      }
    },
    [roundState, nextSlide, currentSlide, game, metrics, autoSave, now],
  );

  useEffect(() => {
    if (!moderationTooltipHasBeenShown && !showModerationTooltip && roundState === 'in') {
      setModerationTooltipHasBeenShown(true);
      delay(() => setShowModerationTooltip(true), 4000);
      delay(() => setShowModerationTooltip(false), 30000);
    }
    //TODO fix this properly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundState]);

  const shouldShowVotingButtonText = useMemo(() => {
    if (
      (currentSlide.type === 'answers' || currentSlide.type === 'canvas') &&
      nextSlide?.type === 'voting'
    ) {
      return roundState === 'post-between';
    }
    return false;
  }, [currentSlide.type, nextSlide?.type, roundState]);

  return (
    <>
      <Hotkeys keyName="space, enter " onKeyUp={handleEnterPressed} />

      {/* Summary of number of words/votes after a round */}
      {roundState === 'post-between' && (
        <ActivitySpecificResponseSummary
          currentSlide={currentSlide}
          gameId={game.id}
          tenantId={tenantId}
        />
      )}

      <LiveGameWidgetContainer>
        {/* Counter for response */}
        {roundState === 'in' && !!secondsLeft && (
          <LiveCounterWidget
            currentSlide={currentSlide}
            gameId={game.id}
            roundState={roundState}
            tenantId={tenantId}
          />
        )}
        {/* Timer widget for adjusting time and start/stop activities */}

        {currentSlide.type !== 'slide' &&
          (roundState === 'pre' ||
            roundState === 'in' ||
            roundState === 'post-between') &&
          !!secondsLeft && (
            <TimerWidgetContainer $isFrozen={isFrozen}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: roundState === 'in' ? 'center' : 'space-between',
                  borderBottom:
                    roundState === 'in' ? 'none' : `solid 1px ${theme.colors.grey}`,
                  height: 'fit-content',
                  paddingBottom: '10px',
                }}
              >
                {roundState !== 'post-between' || nextSlide?.type === 'voting' ? (
                  <>
                    <LiveCountdown
                      secondsLeft={secondsLeft}
                      animate={secondsLeft < 10}
                      isFrozen={isFrozen && roundState === 'in'}
                      fontColor={secondsLeft < 10 ? '#ff6054' : theme.colors.white}
                      fontSize={secondsLeft >= 6000 ? '60px' : `72px`}
                    />
                    {roundState !== 'in' && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'space-around',
                          height: '100%',
                          gap: 10,
                        }}
                      >
                        <Tooltip placement="left" title={'Add 30 seconds'}>
                          <TimerButton
                            type="ghost"
                            onClick={e => {
                              metrics.logEvent(
                                'LiveGame.AddTimeBeforePlay.Increase.Clicked',
                              );
                              if (secondsLeft >= 300) {
                                debounceEventIfNotTrusted({
                                  cb: () => handleEditSlideDuration(+60),
                                  isTrusted: e.isTrusted,
                                });

                                return;
                              }
                              if (secondsLeft >= 60) {
                                debounceEventIfNotTrusted({
                                  cb: () => handleEditSlideDuration(+30),
                                  isTrusted: e.isTrusted,
                                });

                                return;
                              }

                              debounceEventIfNotTrusted({
                                cb: () => handleEditSlideDuration(+10),
                                isTrusted: e.isTrusted,
                              });
                            }}
                          >
                            <PlusSvg />
                          </TimerButton>
                        </Tooltip>
                        <Tooltip placement="left" title={'Remove 30 seconds'}>
                          <TimerButton
                            type="ghost"
                            onClick={e => {
                              metrics.logEvent(
                                'LiveGame.AddTimeBeforePlay.Decrease.Clicked',
                              );
                              if (secondsLeft >= 300) {
                                debounceEventIfNotTrusted({
                                  cb: () => handleEditSlideDuration(-60),
                                  isTrusted: e.isTrusted,
                                });

                                return;
                              }
                              if (secondsLeft > 60) {
                                debounceEventIfNotTrusted({
                                  cb: () => handleEditSlideDuration(-30),
                                  isTrusted: e.isTrusted,
                                });

                                return;
                              }
                              if (secondsLeft > 15)
                                debounceEventIfNotTrusted({
                                  cb: () => handleEditSlideDuration(-10),
                                  isTrusted: e.isTrusted,
                                });
                            }}
                          >
                            <IconWrapper>
                              <MinusSvg />
                            </IconWrapper>
                          </TimerButton>
                        </Tooltip>
                      </div>
                    )}
                  </>
                ) : (
                  <span style={{ width: '100%', fontSize: '20px', textAlign: 'center' }}>
                    <T keyName="Well done!" defaultValue="Well done!" />
                  </span>
                )}
              </div>
              {(roundState === 'post-between' && nextSlide?.type !== 'voting') ||
              (showAIFeedbackOverlay && isAIFeedbackResultsSlide) ? (
                <OrangeButton
                  styles={{ margin: '10px 0px 0px 0px', gridColumn: '1/3' }}
                  onClick={e => {
                    debounceEventIfNotTrusted({
                      cb: handleSeeResults,
                      isTrusted: e.isTrusted,
                    });
                  }}
                >
                  {currentSlide.type === 'aiAssessedAnswers' ||
                  currentSlide.type === 'aiAssessedImageAnswers' ? (
                    showAIFeedbackOverlay ? (
                      <T keyName="Next" defaultValue="Next" />
                    ) : (
                      <T keyName="Give Feedback" defaultValue="Give Feedback" />
                    )
                  ) : isAIFeedbackResultsSlide && showAIFeedbackOverlay ? (
                    <T keyName="Next" defaultValue="Next" />
                  ) : (
                    <T keyName="Results" defaultValue="See Results" />
                  )}
                </OrangeButton>
              ) : (
                <>
                  <TimerButtonContainer>
                    {game.roundStartsAt && roundState !== 'post-between' && (
                      <div
                        style={{
                          display: 'flex',
                          gap: 10,
                          alignItems: 'center',
                          width: '100%',
                          padding: '5px',
                          height: '100%',
                        }}
                      >
                        <TimerButton
                          type="secondary"
                          icon={
                            <IconWrapper width={'14px'} height={'14px'}>
                              {' '}
                              <PlusSvg />{' '}
                            </IconWrapper>
                          }
                          styles={{
                            display: 'flex',
                            gap: '5px',
                            borderRadius: '5px',
                            width: 'fit-content',
                            fontSize: '14px',
                          }}
                          onClick={e => {
                            metrics.logEvent('LiveGame.AddTimeDuringPlay.Clicked');
                            debounceEventIfNotTrusted({
                              cb: () => handleEditSlideDuration(60),
                              isTrusted: e.isTrusted,
                            });
                          }}
                        >
                          <T keyName="1 min" defaultValue="1 min" />
                        </TimerButton>
                        <TimerButton
                          type="secondary"
                          icon={
                            <IconWrapper width={'14px'} height={'14px'}>
                              {' '}
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 14 14"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M12.6 0H1.4C0.6286 0 0 0.6286 0 1.4V12.6C0 13.3714 0.6286 14 1.4 14H12.6C13.3714 14 14 13.3714 14 12.6V1.4C14 0.6286 13.3714 0 12.6 0Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </IconWrapper>
                          }
                          styles={{
                            display: 'flex',
                            gap: '5px',
                            borderRadius: '5px',
                            width: '100%',
                            fontSize: '14px',
                          }}
                          onClick={e => {
                            if (game.roundStartsAt) {
                              metrics.logEvent('LiveGame.EndActivity.Clicked');
                              debounceEventIfNotTrusted({
                                cb: handleEndSlide,
                                isTrusted: e.isTrusted,
                              });
                            }
                          }}
                        >
                          {currentSlide.type === 'voting' ? (
                            <T keyName="End voting" defaultValue="End voting" />
                          ) : (
                            <T keyName="End activity" defaultValue="End activity" />
                          )}
                        </TimerButton>
                      </div>
                    )}
                  </TimerButtonContainer>

                  <TimerButtonContainer>
                    {game.roundStartsAt &&
                    roundState !== 'post-between' &&
                    !game.roundFreezedAt ? null : (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: '100%',
                          paddingTop: '10px',
                          height: '100%',
                        }}
                      >
                        <OrangeButton
                          onClick={e => {
                            metrics.logEvent('LiveGame.StartActivity.Click', {
                              isTrusted: e.isTrusted,
                              clickType: e.type,
                              isStartVoting: shouldShowVotingButtonText,
                              isStartActivity: !shouldShowVotingButtonText,
                            });
                            debounceEventIfNotTrusted({
                              cb: handleStartPressed,
                              isTrusted: e.isTrusted,
                            });
                          }}
                        >
                          {shouldShowVotingButtonText ? (
                            <T keyName="Start voting" defaultValue="Start voting" />
                          ) : (
                            <T keyName="Start activity" defaultValue="Start activity" />
                          )}
                        </OrangeButton>
                      </div>
                    )}
                  </TimerButtonContainer>
                </>
              )}
            </TimerWidgetContainer>
          )}
        {showAIFeedbackOverlay &&
          isAIFeedbackResultsSlide &&
          currentSlide.type === 'responsegrid' && (
            <div
              style={{
                marginTop: '10px',
                width: '100%',
                backgroundColor: theme.colors.translucentLightBlack,
                padding: '10px',
                borderRadius: '10px',
              }}
            >
              <OrangeButton
                onClick={e => {
                  debounceEventIfNotTrusted({
                    cb: handleSeeResults,
                    isTrusted: e.isTrusted,
                  });
                }}
              >
                <T keyName="Next" defaultValue="Next" />
              </OrangeButton>
            </div>
          )}
        {/* Show Next button after overlay is closed, until at least one modal has been opened */}
        {!showAIFeedbackOverlay &&
          isAIFeedbackResultsSlide &&
          currentSlide.type === 'responsegrid' &&
          !modalOpened &&
          responses &&
          responses.length > 0 &&
          slideIdForAiScores && (
            <div
              style={{
                marginTop: '10px',
                width: '100%',
                backgroundColor: theme.colors.translucentLightBlack,
                padding: '10px',
                borderRadius: '10px',
              }}
            >
              <OrangeButton
                onClick={e => {
                  debounceEventIfNotTrusted({
                    cb: () => {
                      openFirstAiResponse(game.id, slideIdForAiScores);
                    },
                    isTrusted: e.isTrusted,
                  });
                }}
              >
                <T keyName="Next" defaultValue="Next" />
              </OrangeButton>
            </div>
          )}
      </LiveGameWidgetContainer>
    </>
  );
};

const OrangeButton = styled(Button)`
  background-color: ${theme.colors.orange};
  width: 100%;
  height: 42px;
  border-radius: 5px;

  span {
    font-size: 20px;
  }

  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.orangeDark};
  }
`;

const LiveGameWidgetContainer = styled.div`
  position: fixed;
  right: 10px;
  bottom: 85px;
  color: ${theme.colors.white};
  width: fit-content;
  gap: 0;
  display: grid;
  grid-template-rows: auto;
  justify-content: flex-end;
  justify-items: end;
  grid-auto-flow: dense;
  align-items: center;
  border-radius: 5px;
  z-index: ${zIndexes('LIVEGAME_TIMER_WIDGET')};
`;
const TimerButtonContainer = styled.div`
  align-items: center;
  justify-content: center;
  height: 100%;
`;
const TimerWidgetContainer = styled.div<{ $isFrozen: boolean }>`
  background-color: ${theme.colors.translucentLightBlack};
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  padding: 10px;
  border-radius: 5px;
  width: 270px;
  height: fit-content;
  position: relative;
  overflow: hidden;
  ${({ $isFrozen }) =>
    $isFrozen &&
    css`
      &:after {
        background: #fff;
        content: '';
        height: 230px;
        left: -130px;
        opacity: 0.2;
        position: absolute;
        top: -50px;
        transform: rotate(35deg);
        transition: all 550ms cubic-bezier(0.19, 1, 0.22, 1);
        width: 50px;
        z-index: -10;
      }
      background-color: rgba(51, 160, 221, 0.5);
      &:hover {
        background-color: rgba(51, 160, 221, 0.5);
        &:after {
          left: 130%;
          transition: all 550ms cubic-bezier(0.19, 1, 0.22, 1);
        }
      }
    `}
`;

const TimerButton = styled(Button)`
  border: solid 1px #b1b1b1;
  color: ${theme.colors.white};
  border-radius: 5px;
  font-size: 16px;
  padding: 10px;
  svg {
    margin: 0;
    width: 16px;
    height: 16px;
  }
  span {
    font-size: 16px;
  }
  transition: 0.2s;
  &:active,
  &:focus,
  &:hover {
    color: ${theme.colors.white};
    border: solid 1px #b1b1b1;
    background: rgba(255, 255, 255, 0.1) !important;
  }
`;

export default SimpleLiveGameWidgetV2;
